import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import type {
  Skillset,
  SkillsetMaintainer,
  PackageFileInfo,
  PackageFileContent,
} from '@/api/apiClient/skillsets';

import * as skillsetsApi from '@/api/apiClient/skillsets';

// Vouch statuses that hide an entity from every public read surface. Setting one
// makes the public detail endpoint return 404, so the store must not re-fetch.
const HIDDEN_VOUCH_STATUSES = ['pending', 'denied'];

export const useSkillsetsStore = defineStore('skillsets', () => {
  const skillsets = ref<Array<Skillset>>([]);
  const searchResults = ref<Array<Skillset>>([]);
  const currentSkillset = ref<Skillset | null>(null);
  const loading = ref(false);

  // Additional state for skillset details
  const maintainers = ref<Array<SkillsetMaintainer>>([]);
  const codeFiles = ref<Array<PackageFileInfo>>([]);

  // Clear related data when currentSkillset changes
  watch(currentSkillset, (newSkillset, oldSkillset) => {
    if (newSkillset?.name !== oldSkillset?.name) {
      maintainers.value = [];
      codeFiles.value = [];
    }
  });

  /**
   * Fetch all skillsets
   */
  const fetchSkillsets = async () => {
    loading.value = true;
    try {
      skillsets.value = await skillsetsApi.listSkillsets();
    } finally {
      loading.value = false;
    }
  };

  /**
   * Search for skillsets by query
   *
   * @param query - Search query string
   */
  const searchSkillsets = async (query: string) => {
    loading.value = true;
    try {
      searchResults.value = await skillsetsApi.searchSkillsets(query);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch detailed information for a specific skillset
   *
   * @param name - Skillset name
   */
  const fetchSkillsetDetail = async (name: string) => {
    loading.value = true;
    try {
      currentSkillset.value = await skillsetsApi.getSkillset(name);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Clear search results
   */
  const clearSearchResults = () => {
    searchResults.value = [];
  };

  /**
   * Set vouch status for a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.vouchStatus - The vouch status to set
   * @param args.reason - The denial reason; required by the server when denying
   */
  const setVouchStatus = async (args: {
    skillsetName: string;
    vouchStatus: string;
    reason?: string | null;
  }) => {
    const { skillsetName, vouchStatus, reason } = args;
    const updated = await skillsetsApi.setSkillsetVouchStatus({
      skillsetName,
      vouchStatus,
      reason,
    });
    if (HIDDEN_VOUCH_STATUSES.includes(vouchStatus)) {
      if (currentSkillset.value?.name === skillsetName) {
        currentSkillset.value = {
          ...currentSkillset.value,
          vouchStatus: updated.vouchStatus,
        };
      }
      return;
    }
    await fetchSkillsetDetail(skillsetName);
  };

  /**
   * Approve or deny a skillset and all of its bundled skills/subagents in a
   * single action. A deny moves the skillset to a hidden status, so (like
   * setVouchStatus) the now-404ing detail is patched locally rather than
   * re-fetched; an approve makes it publicly visible and re-fetches.
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.decision - 'approve' or 'deny'
   * @param args.reason - The denial reason; required by the server when denying
   *
   * @returns The per-constituent review result and a changed/skipped summary
   */
  const reviewSkillset = async (args: {
    skillsetName: string;
    decision: 'approve' | 'deny';
    reason?: string | null;
  }) => {
    const { skillsetName, decision, reason } = args;
    const result = await skillsetsApi.reviewSkillset({
      skillsetName,
      decision,
      reason,
    });

    if (HIDDEN_VOUCH_STATUSES.includes(result.skillset.vouchStatus)) {
      if (currentSkillset.value?.name === skillsetName) {
        currentSkillset.value = {
          ...currentSkillset.value,
          vouchStatus: result.skillset.vouchStatus,
        };
      }
    } else {
      await fetchSkillsetDetail(skillsetName);
    }

    return result;
  };

  /**
   * Update metadata for a skillset version
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.version - The version to update
   * @param args.description - Optional description
   * @param args.releaseNotes - Optional release notes
   * @param args.keywords - Optional keywords
   */
  const updateMetadata = async (args: {
    skillsetName: string;
    version: string;
    description?: string | null;
    releaseNotes?: string | null;
    keywords?: Array<string> | null;
  }) => {
    const { skillsetName, version, description, releaseNotes, keywords } =
      args;
    await skillsetsApi.updateSkillsetMetadata({
      skillsetName,
      version,
      description: description ?? undefined,
      releaseNotes: releaseNotes ?? undefined,
      keywords: keywords ?? undefined,
    });
    await fetchSkillsetDetail(skillsetName);
  };

  /**
   * Delete a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name to delete
   */
  const deleteSkillset = async (args: { skillsetName: string }) => {
    const { skillsetName } = args;
    await skillsetsApi.deleteSkillset({ skillsetName });
    currentSkillset.value = null;
  };

  /**
   * Set featured status for a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.featured - Whether the skillset should be featured
   */
  const setFeatured = async (args: {
    skillsetName: string;
    featured: boolean;
  }) => {
    const { skillsetName, featured } = args;
    await skillsetsApi.setSkillsetFeatured({ skillsetName, featured });
    await fetchSkillsetDetail(skillsetName);
  };

  /**
   * Rename a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - Current skillset name
   * @param args.newName - New skillset name
   */
  const renameSkillset = async (args: {
    skillsetName: string;
    newName: string;
  }) => {
    const { skillsetName, newName } = args;
    await skillsetsApi.renameSkillset({ skillsetName, newName });
    if (currentSkillset.value) {
      currentSkillset.value.name = newName;
    }
  };

  /**
   * Fetch maintainers for a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   */
  const fetchMaintainers = async (args: { skillsetName: string }) => {
    const { skillsetName } = args;
    const result = await skillsetsApi.fetchSkillsetMaintainers(skillsetName);
    maintainers.value = result.maintainers;
  };

  /**
   * Add a maintainer to a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.email - Email of user to add
   * @param args.role - Role to assign
   */
  const addMaintainer = async (args: {
    skillsetName: string;
    email: string;
    role: 'owner' | 'maintainer';
  }) => {
    const { skillsetName, email, role } = args;
    await skillsetsApi.addSkillsetMaintainer({ skillsetName, email, role });
    await fetchMaintainers({ skillsetName });
  };

  /**
   * Remove a maintainer from a skillset
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.email - Email of user to remove
   */
  const removeMaintainer = async (args: {
    skillsetName: string;
    email: string;
  }) => {
    const { skillsetName, email } = args;
    await skillsetsApi.removeSkillsetMaintainer({ skillsetName, email });
    await fetchMaintainers({ skillsetName });
  };

  /**
   * Reingest metadata for a skillset version
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.version - The version to reingest
   */
  const reingestMetadata = async (args: {
    skillsetName: string;
    version: string;
  }) => {
    const { skillsetName, version } = args;
    await skillsetsApi.reingestSkillsetMetadata({ skillsetName, version });
    await fetchSkillsetDetail(skillsetName);
  };

  /**
   * Fetch code files for a skillset version
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.version - The version
   */
  const fetchCodeFiles = async (args: {
    skillsetName: string;
    version: string;
  }) => {
    const { skillsetName, version } = args;
    const result = await skillsetsApi.getSkillsetFiles({
      name: skillsetName,
      version,
    });
    codeFiles.value = result.files;
  };

  /**
   * Fetch content for a specific file
   *
   * @param args - Arguments
   * @param args.skillsetName - The skillset name
   * @param args.version - The version
   * @param args.filePath - The file path
   *
   * @returns Promise resolving to file content
   */
  const fetchFileContent = async (args: {
    skillsetName: string;
    version: string;
    filePath: string;
  }): Promise<PackageFileContent> => {
    const { skillsetName, version, filePath } = args;
    return skillsetsApi.getSkillsetFileContent({
      name: skillsetName,
      version,
      filePath,
    });
  };

  return {
    skillsets,
    searchResults,
    currentSkillset,
    loading,
    maintainers,
    codeFiles,
    fetchSkillsets,
    searchSkillsets,
    fetchSkillsetDetail,
    clearSearchResults,
    setVouchStatus,
    reviewSkillset,
    updateMetadata,
    deleteSkillset,
    setFeatured,
    renameSkillset,
    fetchMaintainers,
    addMaintainer,
    removeMaintainer,
    reingestMetadata,
    fetchCodeFiles,
    fetchFileContent,
  };
});
